import { useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Add, Delete } from '@mui/icons-material';
import { Button, Card, Container, LinearProgress, Stack, Typography } from '@mui/material';
import { FormSubmitRow } from 'components/common/form/submit-row';
import { TooltipSquareIconButton } from 'components/common/icon/tooltip-icon-button';
import { FormSwitch } from 'components/views/forms/form-checkbox';
import { FormTextField } from 'components/views/forms/form-text-field';
import { CURRENT_AVAILABLE_GROUPS, useHasAuthGroup } from 'providers/auth';
import { useSnackbar } from 'providers/snackbar';
import { FC } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import {
  CampaignPosting as ICampaignPosting,
  CampaignPosting_campaign,
  CampaignPostingVariables,
} from 'types/apollo/CampaignPosting';
import {
  CampaignPostingConfigUpdate,
  CampaignPostingConfigUpdateVariables,
} from 'types/apollo/CampaignPostingConfigUpdate';
import {
  CampaignRequiredMetadataUpdate,
  CampaignRequiredMetadataUpdateVariables,
} from 'types/apollo/CampaignRequiredMetadataUpdate';
import { TelnyxThrottling, TelnyxThrottlingVariables } from 'types/apollo/TelnyxThrottling';
import * as yup from 'yup';
import {
  CampaignPostingConfigUpdateMutation,
  CampaignPostingQuery,
  CampaignRequiredMetadataUpdateMutation,
} from './campaign-posting.graphql';
import { ThrottlingCard } from './throttling-card';
import { TelnyxThrottlingQuery } from './throttling.graphql';

interface DelayFormData {
  isRealtime: boolean;
  delay: string;
}

const delaySchema = yup
  .object({
    isRealtime: yup.boolean().required(),
    delay: yup
      .number()
      .typeError('Must be a number')
      .integer()
      .min(0)
      .max(86400 /* 24h in seconds */)
      .when('isRealtime', {
        is: true,
        then: (s) => s.required('Required'),
        otherwise: (s) => s.notRequired(),
      }),
  })
  .required();

const DelayCard: FC<{
  campaign: CampaignPosting_campaign;
  refetch: () => Promise<unknown>;
  canEdit: boolean;
}> = ({ campaign, refetch, canEdit }) => {
  const { openSnackbar } = useSnackbar();
  const form = useForm<DelayFormData>({
    values: {
      isRealtime: campaign.postingConfiguration.sameDay,
      delay: campaign.postingConfiguration.delay.toString(),
    },
    resolver: yupResolver(delaySchema),
  });
  // Lock inputs while a save is in flight; the post-mutation refetch would
  // otherwise re-sync the reactive `values` prop and clobber any edits the
  // user makes mid-flight.
  const isSubmitting = form.formState.isSubmitting;
  const inputsDisabled = !canEdit || isSubmitting;
  const isRealtime = form.watch('isRealtime');
  const [updatePostingConfig] = useMutation<
    CampaignPostingConfigUpdate,
    CampaignPostingConfigUpdateVariables
  >(CampaignPostingConfigUpdateMutation);

  const onSubmit = form.handleSubmit(async ({ isRealtime: nextIsRealtime, delay }) => {
    await updatePostingConfig({
      variables: {
        campaignPostingConfigUpdateData: {
          campaignId: campaign.id,
          delay: nextIsRealtime ? Number(delay) : null,
          sameDay: nextIsRealtime,
        },
      },
    });
    openSnackbar({ message: 'Posting updated', severity: 'success' });
    // Refetch is best-effort: the save already succeeded. A refetch failure
    // is reported by the global Apollo onError link; the form may briefly
    // show stale values until next navigation.
    try {
      await refetch();
    } catch {
      /* see comment above */
    }
  });

  return (
    <Card variant="outlined" sx={{ padding: '16px' }}>
      <form onSubmit={onSubmit}>
        <FormProvider {...form}>
          <Stack spacing={2}>
            <Typography variant="h5">Posting</Typography>
            <FormSwitch
              form={form}
              name="isRealtime"
              label="Is Realtime"
              disabled={inputsDisabled}
            />
            {isRealtime && (
              <FormTextField
                form={form}
                name="delay"
                type="number"
                label="Posting Delay"
                helperText="Time to delay posting in seconds"
                variant="outlined"
                disabled={inputsDisabled}
                inputProps={{ min: 0, step: 1 }}
                sx={{ maxWidth: '150px' }}
              />
            )}
            {canEdit && <FormSubmitRow onCancel={() => form.reset()} />}
          </Stack>
        </FormProvider>
      </form>
    </Card>
  );
};

interface RequiredMetadataFormData {
  requiredMetadata: { value: string }[];
}

const requiredMetadataSchema = yup
  .object({
    requiredMetadata: yup
      .array()
      .of(
        yup
          .object({
            value: yup
              .string()
              .trim()
              .min(1, 'Required')
              .max(128, 'Max 128 characters')
              .required('Required'),
          })
          .required(),
      )
      // Array-level test: flags every duplicate row (the first occurrence
      // stays clean; every subsequent one shows "Duplicate key"). yup
      // aggregates multiple errors when we return a ValidationError whose
      // first arg is an array.
      .test('no-duplicates', function (rows) {
        if (!Array.isArray(rows)) return true;
        const seen = new Map<string, number>();
        const errors: yup.ValidationError[] = [];
        for (let i = 0; i < rows.length; i++) {
          const v = rows[i]?.value?.trim();
          if (!v) continue;
          if (seen.has(v)) {
            errors.push(
              this.createError({
                path: `${this.path}[${i}].value`,
                message: 'Duplicate key',
              }),
            );
          } else {
            seen.set(v, i);
          }
        }
        if (errors.length === 0) return true;
        return new yup.ValidationError(errors);
      })
      .required(),
  })
  .required();

const RequiredMetadataCard: FC<{
  campaign: CampaignPosting_campaign;
  refetch: () => Promise<unknown>;
  canEdit: boolean;
}> = ({ campaign, refetch, canEdit }) => {
  const { openSnackbar } = useSnackbar();
  // Use defaultValues (not the reactive `values` prop) because useFieldArray
  // mutations (append/remove) need internal state to be the source of truth —
  // a reactive `values` would re-sync on every render and clobber the user's
  // row removals. We manually reset to the new state after a successful save.
  const form = useForm<RequiredMetadataFormData>({
    defaultValues: {
      requiredMetadata: (campaign.requiredMetadata ?? []).map((value) => ({ value })),
    },
    resolver: yupResolver(requiredMetadataSchema),
    mode: 'onChange',
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'requiredMetadata',
  });
  const isSubmitting = form.formState.isSubmitting;
  const inputsDisabled = !canEdit || isSubmitting;
  const [updateRequiredMetadata] = useMutation<
    CampaignRequiredMetadataUpdate,
    CampaignRequiredMetadataUpdateVariables
  >(CampaignRequiredMetadataUpdateMutation);

  const onSubmit = form.handleSubmit(async ({ requiredMetadata }) => {
    const keys = requiredMetadata.map((row) => row.value.trim());
    await updateRequiredMetadata({
      variables: {
        campaignRequiredMetadataUpdateData: {
          campaignId: campaign.id,
          requiredMetadata: keys.length === 0 ? null : keys,
        },
      },
    });
    form.reset({ requiredMetadata: keys.map((value) => ({ value })) });
    openSnackbar({ message: 'Required metadata updated', severity: 'success' });
    // Refetch is best-effort: the save already succeeded and the form is
    // already reset to the saved keys. See DelayCard for rationale.
    try {
      await refetch();
    } catch {
      /* see comment above */
    }
  });

  return (
    <Card variant="outlined" sx={{ padding: '16px' }}>
      <form onSubmit={onSubmit}>
        <FormProvider {...form}>
          <Stack spacing={2}>
            <Typography variant="h5">Required Metadata</Typography>
            <Typography variant="body2" color="text.secondary">
              Metadata keys that posted leads must include. Leave empty to allow any metadata.
            </Typography>
            {fields.map((field, index) => (
              <Stack key={field.id} direction="row" spacing={1} alignItems="center">
                <FormTextField
                  form={form}
                  name={`requiredMetadata.${index}.value`}
                  disabled={inputsDisabled}
                  label={`Key ${index + 1}`}
                  sx={{ width: 400 }}
                  autoFocus={field.value === ''}
                />
                {canEdit && (
                  <TooltipSquareIconButton
                    tooltip="Remove"
                    onClick={() => remove(index)}
                    iconButtonProps={{ disabled: isSubmitting }}
                  >
                    <Delete />
                  </TooltipSquareIconButton>
                )}
              </Stack>
            ))}
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => append({ value: '' })}
                disabled={isSubmitting}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add required key
              </Button>
            )}
            {canEdit && <FormSubmitRow onCancel={() => form.reset()} />}
          </Stack>
        </FormProvider>
      </form>
    </Card>
  );
};

export const CampaignPosting: FC = () => {
  const { campaignId: id } = useParams();
  const isAdmin = useHasAuthGroup(CURRENT_AVAILABLE_GROUPS.Admin);
  const { data, loading, refetch } = useQuery<ICampaignPosting, CampaignPostingVariables>(
    CampaignPostingQuery,
    { variables: { campaignId: id ?? '0' } },
  );
  const {
    data: throttlingData,
    loading: throttlingLoading,
    refetch: refetchThrottling,
  } = useQuery<TelnyxThrottling, TelnyxThrottlingVariables>(TelnyxThrottlingQuery, {
    variables: { campaignId: id ?? '' },
    skip: !id,
  });

  const campaign = data?.campaign;
  // `data === undefined` means the first fetch hasn't completed; refetches keep
  // `data` populated so the progress bar doesn't flash on save. `throttlingData`
  // can be `{ telnyxThrottling: null }` for unprovisioned campaigns — that
  // still counts as "loaded".
  const ready = !!campaign && throttlingData !== undefined;

  return (
    <Container maxWidth="lg" sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={2}>
        {!ready && (loading || throttlingLoading) && <LinearProgress sx={{ height: '2px' }} />}
        {ready && campaign && throttlingData && id && (
          <>
            <RequiredMetadataCard
              key={campaign.id}
              campaign={campaign}
              refetch={refetch}
              canEdit={isAdmin}
            />
            <DelayCard campaign={campaign} refetch={refetch} canEdit={isAdmin} />
            <ThrottlingCard
              campaignId={id}
              throttling={throttlingData.telnyxThrottling}
              refetch={refetchThrottling}
              canEdit={isAdmin}
            />
          </>
        )}
      </Stack>
    </Container>
  );
};
